import { useMemo } from 'react';
import { useMeasure } from 'react-use';
import { Content } from 'src/contexts/ContentContext';
import { Switch } from 'src/core/interim/Switch';
import { PosFormField } from 'src/core/POS/PosFormField';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, Listing } from 'src/WebApiController';

import { GroupsPreview } from '../GroupListingsV2/components/GroupsPreview';
import { EventGroupings } from './components/EventGroupings';
import { GroupListingsBodyProps } from './components/groupingTypes';
import {
  flattenListingGroup,
  getListingsForMergeListingGroupInput,
} from './components/groupingUtils';
import { ListingGroupInput } from './components/ListingGroupInput';
import { useFullySoldFilter } from './GroupListingsBody.hook';

export const GroupListingsBody = ({
  events,
  listingGroup,
  onEventSelected,
}: GroupListingsBodyProps) => {
  const {
    excludeFullySold,
    showExcludeFullySold,
    sanitizedEvents,
    mergeListingGroupInputs,
    onToggleExcludeFullySold,
  } = useFullySoldFilter(events, listingGroup);

  const [containerRef, { height: containerHeight }] =
    useMeasure<HTMLDivElement>();

  const hasGroupWizardInEditFeature = useUserHasFeature(
    Feature.GroupWizardInEdit
  );

  const groupListings = useMemo(() => {
    if (listingGroup && mergeListingGroupInputs?.length === 1) {
      return (
        sanitizedEvents
          .flatMap(
            ({ entities }) =>
              entities.listings
                ?.flatMap(flattenListingGroup)
                .map((l) => l as Listing) ?? []
          )
          .filter((listing) => listing?.id > 0) ?? []
      );
    }
    return [];
  }, [sanitizedEvents, listingGroup, mergeListingGroupInputs?.length]);

  return (
    <Stack
      direction="column"
      gap={listingGroup && hasGroupWizardInEditFeature ? undefined : 'xl'}
      height="full"
    >
      {showExcludeFullySold && (
        <PosFormField
          label={<Content id={ContentId.ExcludeFullySoldListings} />}
        >
          <Switch
            checked={excludeFullySold}
            onChange={onToggleExcludeFullySold}
          />
        </PosFormField>
      )}
      <Stack
        ref={containerRef}
        direction="column"
        style={{ height: listingGroup ? '100%' : '90%' }}
      >
        {listingGroup ? (
          hasGroupWizardInEditFeature ? (
            <GroupsPreview
              eventListings={groupListings}
              groups={mergeListingGroupInputs}
            />
          ) : (
            <ListingGroupInput
              key={`mergeListingGroupInput-${0}`}
              mergeListingGroupInputIndex={0}
              listings={getListingsForMergeListingGroupInput(
                mergeListingGroupInputs[0],
                sanitizedEvents
              )}
            />
          )
        ) : (
          <EventGroupings
            events={sanitizedEvents}
            onEventSelected={onEventSelected}
            containerHeight={containerHeight}
          />
        )}
      </Stack>
    </Stack>
  );
};
